import { Controller } from "@hotwired/stimulus"

// Gates a guided-step submit button: it stays disabled (grayed) until the step's
// input is satisfied, so a visitor can't advance an empty answer. The button is
// rendered disabled and we enable it once the form is ready — connect() runs the
// first check so a resumed step with a prefilled value starts enabled.
//
// Native constraint validation (checkValidity) already covers required text,
// file, and radio inputs. Two things it can't express, layered on top:
//   • trim  — whitespace-only text shouldn't count as a real answer.
//   • anyOf — steps needing at least one of several optional fields (the contact
//             step: email OR phone). Tag those inputs with the `anyOf` target.
export default class extends Controller {
  static targets = ["submit", "anyOf"]
  static values = { submittingLabel: String }

  connect() {
    this.refresh()
  }

  // On turbo:submit-start: lock the button so a slow request (a phone photo on
  // LTE can take 15-30s) reads as "working" instead of broken, and can't be
  // double-submitted. No un-lock needed: every response replaces #imagine-flow,
  // which discards this form wholesale.
  submitStart() {
    this.submitTarget.disabled = true
    if (this.hasSubmittingLabelValue) this.submitTarget.value = this.submittingLabelValue
  }

  refresh() {
    this.submitTarget.disabled = !this.ready()
  }

  ready() {
    if (!this.element.checkValidity()) return false
    if (this.requiredText().some((el) => el.value.trim() === "")) return false
    if (this.hasAnyOfTarget && !this.anyOfTargets.some((el) => el.value.trim() !== "")) return false
    return true
  }

  // Required text-like fields (text, email, tel, textarea — not file/radio/checkbox)
  // so the trim check only applies where a typed value is expected.
  requiredText() {
    return Array.from(this.element.querySelectorAll("input[required], textarea[required]"))
      .filter((el) => !["file", "radio", "checkbox"].includes(el.type))
  }
}
